import {
  getDocumentDownloadUrl,
  getDocumentVersionDownloadUrl,
  getThreadAttachmentDownloadUrl,
} from "../api/rag-api.js";
import type { RagCitation } from "../types.js";
import {
  buildDocumentOpenUrl,
  resolveCitationPage,
  shouldOpenInAppPdfViewer,
} from "./document-open.js";

export type OpenCitationDocumentOptions = {
  title: string;
  initialPage?: number;
  sourceType: "document" | "attachment";
  documentVersionId?: string;
  highlightText?: string;
};

export type OnOpenCitationDocument = (
  sourceId: string,
  options: OpenCitationDocumentOptions,
) => void;

export function hasOpenableCitationSource(citation: RagCitation): boolean {
  return Boolean(citation.documentId || citation.attachmentId);
}

/**
 * Resolve a citation to an in-app PDF viewer or a new-tab download URL.
 * Throws with a user-facing message when the source cannot be opened.
 */
export async function openCitationDocument({
  citation,
  threadId,
  onOpenDocument,
}: {
  citation: RagCitation;
  threadId?: string;
  onOpenDocument?: OnOpenCitationDocument;
}): Promise<void> {
  const isAttachment = Boolean(citation.attachmentId);
  const sourceId = isAttachment ? citation.attachmentId : citation.documentId;

  if (!sourceId) {
    throw new Error("Document reference is unavailable.");
  }

  if (isAttachment && !threadId) {
    throw new Error("Thread context is unavailable for this attachment.");
  }

  const resolvedPage = resolveCitationPage(citation);

  const { downloadUrl, mimeType } = isAttachment
    ? await getThreadAttachmentDownloadUrl(threadId!, sourceId)
    : citation.documentVersionId && citation.documentId
      ? await getDocumentVersionDownloadUrl(citation.documentId, citation.documentVersionId)
      : await getDocumentDownloadUrl(sourceId);

  if (onOpenDocument && shouldOpenInAppPdfViewer(mimeType, citation.documentTitle, downloadUrl)) {
    onOpenDocument(sourceId, {
      title: citation.documentTitle,
      initialPage: resolvedPage,
      sourceType: isAttachment ? "attachment" : "document",
      documentVersionId: citation.documentVersionId,
      highlightText: citation.excerpt,
    });
    return;
  }

  const openUrl = buildDocumentOpenUrl(downloadUrl, {
    mimeType,
    page: resolvedPage ?? citation.page,
  });
  window.open(openUrl, "_blank", "noopener,noreferrer");
}
